import { call, put, select, takeEvery } from 'typed-redux-saga';

import { sendCloudApiCommand } from '../../../../shared/features/moderation/sagas';
import { openConfirmNotificationAction } from '../../../../shared/features/notifications/confirm-notification';
import { NOTIFICATIONS_MAP, notify } from '../../../../shared/features/notifications/ps-notifications';
import { loadPermissionsExtendedMatrixAction } from '../../../../shared/features/permissions-extended';
import { i18n } from '../../../../shared/helpers/i18n';
import { createLogger } from '../../../../shared/helpers/loggerFacade';
import { Role, RolePermission } from '../../../../shared/helpers/permissions';
import { cloudApi } from '../../../../shared/helpers/transport';
import { CloudAPI } from '../../../../shared/helpers/transport/cloud-api/types';
import { getConferenceConfig, selectIsPermissionsExtendedActive } from '../../selectors';
import { NotificationId } from '../../types';
import { stopRecordConferenceAction } from '../record/slice';
import { getMembersPermissions, getPermissionsVersion } from './selectors';
import {
    setCalendarSummarizationForMembersAllowed,
    setCloudRecordingForMembersAllowed,
    setRecordingForMembersAllowed,
    setSummarizationForMembersAllowed,
    updateLocalMembersPermissions,
} from './slice';
import { updatePermissionsInfoSaga } from './update-permissions-saga';

const logger = createLogger('permissions-sagas');

type RevokedPermissions =
    | RolePermission.RECORDING
    | RolePermission.CLOUD_RECORDING
    | RolePermission.SUMMARIZATION
    | RolePermission.CALENDAR_SUMMARIZATION_RECEIVE;

const COMMANDS_MAP: { [k in RevokedPermissions]: CloudAPI.SendCommand.ApiCommand } = {
    [RolePermission.RECORDING]: 'mute-all-recordings-permanent',
    [RolePermission.CLOUD_RECORDING]: 'grant-all-cloud-recording',
    [RolePermission.SUMMARIZATION]: 'grant-all-summarization',
    [RolePermission.CALENDAR_SUMMARIZATION_RECEIVE]: 'grant-all-calendar-summarization-receive',
};

function* toggleLocalMemberPermission(permission: RevokedPermissions) {
    const currentMemberPermissions = yield* select(getMembersPermissions);

    yield* put(
        updateLocalMembersPermissions(
            currentMemberPermissions?.includes(permission)
                ? currentMemberPermissions?.filter((role) => role !== permission)
                : [...(currentMemberPermissions || []), permission]
        )
    );
}

/**
 * Останавливаем и удаляем запись
 */
export function* recordingPermissionsDeletedSaga() {
    yield* put(stopRecordConferenceAction({ reason: 'permission_deleted', remove: true }));
    yield* put(
        openConfirmNotificationAction({
            headerText: i18n('moderation', 'notification-confirm-ban-recording-header'),
            contentText: i18n('moderation', 'notification-confirm-ban-recording-content'),
        })
    );
}

/**
 * Обновляет права участников
 */
export function* permissionsChangedSaga() {
    const conferenceConfig = yield* select(getConferenceConfig);
    if (!conferenceConfig) {
        logger.event('Conference is not initialized on permissionsChangedSaga', {
            additional: {
                method: 'permissionsChangedSaga',
                feature: 'role_model',
            },
        });
        return;
    }

    const permissionsVersion = yield* select(getPermissionsVersion);
    const requestPermissionsData: CloudAPI.GetPeersState.Request['permissions'] = {
        version: permissionsVersion,
    };

    const payload: CloudAPI.GetPeersState.Request = {
        peers: [],
        permissions: requestPermissionsData,
    };

    const safeLoadMatrix = function* () {
        const isPermissionsExtendedActive = yield* select(selectIsPermissionsExtendedActive);
        if (isPermissionsExtendedActive) {
            yield* put(loadPermissionsExtendedMatrixAction());
        }
    };
    try {
        const peersInfo = yield* call([cloudApi, cloudApi.getPeersState], conferenceConfig.resolved_uri, payload);
        if (!peersInfo.permissions) {
            yield* call(safeLoadMatrix);
            return;
        }

        yield* call(updatePermissionsInfoSaga, peersInfo.permissions);
        yield* call(safeLoadMatrix);
    } catch (error) {
        logger.error(error, {
            method: 'permissionsChangedSaga',
        });
    }
}

/**
 * Переключает пермиссию у обычных участников
 */
function* setPermissionForMembersSaga(permission: RevokedPermissions, allow: boolean) {
    const command = COMMANDS_MAP[permission];
    const options = { role: Role.MEMBER, allow };

    try {
        yield* call(toggleLocalMemberPermission, permission);
        yield* call(sendCloudApiCommand, command, options);
    } catch (error) {
        yield* call(toggleLocalMemberPermission, permission);
        yield* put(notify(NOTIFICATIONS_MAP[NotificationId.PERMISSIONS_ERROR]()));

        logger.error(error, {
            method: 'setPermissionForMembersSaga',
            additional: { ...options, permission, command },
        });
    }
}

/**
 * Переключает возможность локальной записи у обычных участников
 */
function* setRecordingForMembersAllowedSaga({ payload: allow }: ReturnType<typeof setRecordingForMembersAllowed>) {
    yield* call(setPermissionForMembersSaga, RolePermission.RECORDING, allow);
}

/**
 * Переключает возможность облачной записи у обычных участников
 */
function* setCloudRecordingForMembersSaga({ payload: allow }: ReturnType<typeof setCloudRecordingForMembersAllowed>) {
    yield* call(setPermissionForMembersSaga, RolePermission.CLOUD_RECORDING, allow);
}

/**
 * Переключает возможность саммаризации у обычных участников
 */
function* setSummarizationForMembersSaga({ payload: allow }: ReturnType<typeof setSummarizationForMembersAllowed>) {
    yield* call(setPermissionForMembersSaga, RolePermission.SUMMARIZATION, allow);
}

/**
 * Переключает рассылку саммаризации участником приглашенным в календаре
 */
function* setCalendarSummarizationForMembersSaga({
    payload: allow,
}: ReturnType<typeof setSummarizationForMembersAllowed>) {
    yield* call(setPermissionForMembersSaga, RolePermission.CALENDAR_SUMMARIZATION_RECEIVE, allow);
}

export const permissionsSaga = function* () {
    yield* takeEvery(setRecordingForMembersAllowed, setRecordingForMembersAllowedSaga);
    yield* takeEvery(setCloudRecordingForMembersAllowed, setCloudRecordingForMembersSaga);
    yield* takeEvery(setSummarizationForMembersAllowed, setSummarizationForMembersSaga);
    yield* takeEvery(setCalendarSummarizationForMembersAllowed, setCalendarSummarizationForMembersSaga);
};
